import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Tag } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useCategories } from "@/context/SiteContentContext";

const EMPTY = { code: "", kind: "percent", value: "", scope: "all", allow_trade: false, starts_at: "", expires_at: "", max_uses: "", active: true, note: "" };

const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const toIso = (local) => (local ? new Date(local).toISOString() : null);

const statusOf = (c) => {
  const now = Date.now();
  if (c.active === false) return ["Off", "text-muted-foreground"];
  if (c.starts_at && new Date(c.starts_at).getTime() > now) return ["Scheduled", "text-amber-500"];
  if (c.expires_at && new Date(c.expires_at).getTime() < now) return ["Expired", "text-destructive"];
  if (c.max_uses != null && (c.uses || 0) >= c.max_uses) return ["Used up", "text-destructive"];
  return ["Live", "text-primary"];
};

function CodeForm({ initial, onSaved, onCancel }) {
  const cats = useCategories();
  const [f, setF] = useState(initial);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    const body = {
      code: f.code.trim().toUpperCase(), kind: f.kind, value: parseFloat(f.value) || 0, scope: f.scope, allow_trade: !!f.allow_trade,
      starts_at: toIso(f.starts_at), expires_at: toIso(f.expires_at),
      max_uses: f.max_uses === "" || f.max_uses == null ? null : parseInt(f.max_uses, 10) || null,
      active: f.active !== false, note: f.note || null,
    };
    if (!body.code || body.value <= 0) { toast.error("Enter a code and a discount amount"); return; }
    setBusy(true);
    try {
      if (f.id) await api.put(`/admin/discount-codes/${f.id}`, body);
      else await api.post("/admin/discount-codes", body);
      toast.success(`${body.code} saved`);
      onSaved();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  const L = ({ children }) => <label className="mono block text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{children}</label>;
  return (
    <div className="border border-primary/50 bg-primary/5 p-4" data-testid="discount-code-form">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
        <div><L>Code</L><Input data-testid="dc-code" value={f.code} onChange={(e) => set("code", e.target.value.toUpperCase())} placeholder="SUMMER10" className="mt-1 rounded-none mono uppercase" /></div>
        <div>
          <L>Type</L>
          <select data-testid="dc-kind" value={f.kind} onChange={(e) => set("kind", e.target.value)} className="mt-1 block h-10 w-full rounded-none border border-border bg-background px-2 text-sm">
            <option value="percent">Percentage off</option>
            <option value="fixed">Fixed £ off</option>
          </select>
        </div>
        <div><L>{f.kind === "percent" ? "Percent %" : "Amount £"}</L><Input data-testid="dc-value" type="number" step="0.01" min="0" value={f.value} onChange={(e) => set("value", e.target.value)} className="mt-1 rounded-none mono" /></div>
        <div>
          <L>Applies to</L>
          <select data-testid="dc-scope" value={f.scope} onChange={(e) => set("scope", e.target.value)} className="mt-1 block h-10 w-full rounded-none border border-border bg-background px-2 text-sm">
            <option value="all">Whole basket</option>
            <option value="dreamlabs">DreamLabs Flavours only ({cats.build.title} / {cats.deals.title})</option>
            <option value="guest">{cats.guest.title} only</option>
            <option value="economy">{cats.economy.title} only</option>
          </select>
        </div>
        <div><L>Starts (optional)</L><Input data-testid="dc-starts" type="datetime-local" value={f.starts_at} onChange={(e) => set("starts_at", e.target.value)} className="mt-1 rounded-none" /></div>
        <div><L>Expires (optional)</L><Input data-testid="dc-expires" type="datetime-local" value={f.expires_at} onChange={(e) => set("expires_at", e.target.value)} className="mt-1 rounded-none" /></div>
        <div><L>Max uses (blank = unlimited)</L><Input data-testid="dc-max-uses" type="number" min="1" step="1" value={f.max_uses} onChange={(e) => set("max_uses", e.target.value)} placeholder="Unlimited" className="mt-1 rounded-none mono" /></div>
        <div><L>Note (internal)</L><Input data-testid="dc-note" value={f.note || ""} onChange={(e) => set("note", e.target.value)} placeholder="e.g. Instagram promo" className="mt-1 rounded-none" /></div>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-5 text-sm">
        <label className="flex cursor-pointer items-center gap-2"><input type="checkbox" data-testid="dc-allow-trade" checked={!!f.allow_trade} onChange={(e) => set("allow_trade", e.target.checked)} /> Trade accounts can use it too</label>
        <label className="flex cursor-pointer items-center gap-2"><input type="checkbox" data-testid="dc-active" checked={f.active !== false} onChange={(e) => set("active", e.target.checked)} /> Active</label>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" onClick={onCancel} className="rounded-none" data-testid="dc-cancel">Cancel</Button>
          <Button onClick={save} disabled={busy} className="rounded-none" data-testid="dc-save">{f.id ? "Save changes" : "Create code"}</Button>
        </div>
      </div>
    </div>
  );
}

export function DiscountCodesAdmin() {
  const [codes, setCodes] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/discount-codes").then((r) => setCodes(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const remove = async (c) => {
    if (!window.confirm(`Delete code ${c.code}? Customers won't be able to use it any more.`)) return;
    try { await api.delete(`/admin/discount-codes/${c.id}`); toast.success(`${c.code} deleted`); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const toggle = async (c) => {
    try {
      await api.put(`/admin/discount-codes/${c.id}`, { ...c, active: !c.active, code: c.code });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const scopeName = { all: "Whole basket", dreamlabs: "DreamLabs only", guest: "Premium only", economy: "Economy only" };

  return (
    <div className="border border-border p-5" data-testid="discount-codes-section">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 font-heading text-lg font-bold"><Tag className="h-4 w-4 text-primary" /> Discount codes</div>
          <p className="mt-1 text-xs text-muted-foreground">Customers type the code in the basket or at checkout (website and Telegram bot). A use is counted only when an order is placed. Retail only unless you tick "Trade accounts can use it too".</p>
        </div>
        {!editing && <Button onClick={() => setEditing({ ...EMPTY })} className="rounded-none" data-testid="dc-new"><Plus className="mr-1.5 h-4 w-4" /> New code</Button>}
      </div>
      {editing && <div className="mt-4"><CodeForm initial={editing} onSaved={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} /></div>}
      <div className="mt-4 divide-y divide-border border border-border">
        {codes.length === 0 && <div className="p-4 text-sm text-muted-foreground" data-testid="dc-empty">No codes yet.</div>}
        {codes.map((c) => {
          const [status, cls] = statusOf(c);
          return (
            <div key={c.id} className="flex flex-wrap items-center gap-x-5 gap-y-1 p-3 text-sm" data-testid={`dc-row-${c.code}`}>
              <div className="mono w-32 font-bold tracking-[0.1em]">{c.code}</div>
              <div className="w-28 font-medium text-primary">{c.kind === "percent" ? `${c.value}% off` : `£${Number(c.value).toFixed(2)} off`}</div>
              <div className="w-32 text-muted-foreground">{scopeName[c.scope] || c.scope}</div>
              <div className="mono w-28 text-xs text-muted-foreground" data-testid={`dc-uses-${c.code}`}>{c.uses || 0} / {c.max_uses == null ? "∞" : c.max_uses} used</div>
              <div className="mono flex-1 text-[11px] text-muted-foreground">
                {c.starts_at ? `from ${new Date(c.starts_at).toLocaleDateString("en-GB")} ` : ""}{c.expires_at ? `until ${new Date(c.expires_at).toLocaleString("en-GB", { dateStyle: "short", timeStyle: "short" })}` : "no expiry"}{c.allow_trade ? " · trade OK" : ""}{c.note ? ` · ${c.note}` : ""}
              </div>
              <div className={`mono w-16 text-[10px] font-bold uppercase tracking-[0.12em] ${cls}`} data-testid={`dc-status-${c.code}`}>{status}</div>
              <div className="flex items-center gap-1">
                <button type="button" onClick={() => toggle(c)} data-testid={`dc-toggle-${c.code}`} className="mono border border-border px-2 py-1 text-[10px] uppercase tracking-[0.1em] hover:border-foreground">{c.active === false ? "Turn on" : "Turn off"}</button>
                <button type="button" onClick={() => setEditing({ ...c, value: String(c.value), max_uses: c.max_uses == null ? "" : String(c.max_uses), starts_at: toLocalInput(c.starts_at), expires_at: toLocalInput(c.expires_at), note: c.note || "" })} data-testid={`dc-edit-${c.code}`} className="p-1.5 hover:text-primary" aria-label="Edit"><Pencil className="h-3.5 w-3.5" /></button>
                <button type="button" onClick={() => remove(c)} data-testid={`dc-delete-${c.code}`} className="p-1.5 hover:text-destructive" aria-label="Delete"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
